/** @format */

import React, { memo } from "react";
import ReactMarkdown from "react-markdown";
import { AttachFile as AttachFileIcon } from "@mui/icons-material";
import { chatMarkdownComponents, chatRemarkPlugins, chatTextWrapClassName, chatTextWrapStyle } from "../../../utils/chatHelpers";
import AguiMessageBlocks from "@/features/chat-agui/AguiMessageBlocks";
import { isAguiChatMessage } from "@/features/chat-agui/aguiMessageUtils";
import { filterVisibleStreamTools } from "@/features/chat-agui/streamingUtils";
import { TypingIndicatorBubble } from "./TypingIndicator";
import StreamToolCalls from "./StreamToolCalls";
import CollapsibleAgentText from "./CollapsibleAgentText";
import StreamingAgentText from "./StreamingAgentText";
import VoiceMessagePlayer from "../../../components/chat/VoiceMessagePlayer";

const MessageBubble = ({ message, isUser, formatTimestamp, onUiAction, disabled = false }) => {
  if (isUser) {
    return (
      <div className="flex justify-end mb-4">
        <div className="max-w-xs lg:max-w-md min-w-0">
          <div className="bg-[#F7F9FC] text-[#11121A] text-[14px] lg:text-[16px] font-[400] rounded-2xl rounded-br-md px-4 py-3 shadow-sm min-w-0">
            {message.file && (
              <div className="mb-2">
                {message.file.type?.startsWith("image/") ? (
                  <img
                    src={message.file.remoteUrl || message.file.preview}
                    alt="Uploaded"
                    className="max-w-48 max-h-48 rounded-lg object-cover cursor-pointer"
                  />
                ) : (
                  <div className="flex items-center gap-2 text-blue-100">
                    <AttachFileIcon sx={{ fontSize: 16 }} />
                    <span className="text-sm">{message.file.name}</span>
                  </div>
                )}
              </div>
            )}
            <p className="leading-relaxed break-words" style={{ overflowWrap: "anywhere" }}>
              {typeof message.text === "string" ? message.text : JSON.stringify(message.text, null, 2)}
            </p>
            <VoiceMessagePlayer
              chatId={message.chatId}
              messageId={message.id}
              metadata={message.metadata}
            />
          </div>
          <p className="text-xs text-gray-500 text-right mt-1 px-1">{formatTimestamp(message.timestamp)}</p>
        </div>
      </div>
    );
  }

  const hasUi = Array.isArray(message.uiComponents) && message.uiComponents.length > 0;

  const isAgui = isAguiChatMessage(message);

  const hasText = Boolean(typeof message.text === "string" && message.text.trim());

  const visibleTools = filterVisibleStreamTools(message.streamTools);

  const showToolList = isAgui && message.isStreaming && (visibleTools.length > 0 || message.streamStatus === "tool");

  const showStreamingText = message.isStreaming && hasText && (!isAgui || message.streamShowText);

  const showTypingWhileStreaming = message.isStreaming && !showToolList && (isAgui ? !hasUi : !hasText);

  const showFinalText = hasText && !message.isStreaming;

  const useWideLayout = hasUi;

  const textBubbleClass =
    "bg-white border border-gray-200 rounded-2xl rounded-bl-md px-5 py-4 shadow-sm min-w-0 w-full max-w-full overflow-hidden";

  return (
    <div className="flex justify-start mb-4">
      <div
        className={
          useWideLayout
            ? "max-w-[min(100%,100%)] lg:max-w-[min(100%,760px)] min-w-0 w-full overflow-visible"
            : "max-w-[85%] lg:max-w-[640px] min-w-0"
        }
      >
        <div className="flex flex-col items-start gap-2 min-w-0 overflow-visible">
          {showToolList ? <StreamToolCalls tools={visibleTools} activeTool={message.streamTool} /> : null}

          {showTypingWhileStreaming ? <TypingIndicatorBubble className="flex-shrink-0" /> : null}

          {hasUi ? (
            <div className="min-w-0 w-full overflow-visible">
              <AguiMessageBlocks
                uiComponents={message.uiComponents}
                onUiAction={onUiAction}
                disabled={disabled}
              />
            </div>
          ) : null}

          {showStreamingText ? (
            <div className={textBubbleClass}>
              {message.streamShowText ? (
                <div
                  className={`text-[#1D2739] text-[14px] lg:text-[15px] max-w-full ${chatTextWrapClassName}`}
                  style={chatTextWrapStyle}
                >
                  <ReactMarkdown remarkPlugins={chatRemarkPlugins} components={chatMarkdownComponents}>{message.text}</ReactMarkdown>
                </div>
              ) : (
                <StreamingAgentText text={message.text} />
              )}
            </div>
          ) : null}

          {showFinalText ? (
            <div className={textBubbleClass}>
              <div
                className={`text-[#1D2739] text-[14px] lg:text-[15px] max-w-full ${chatTextWrapClassName}`}
                style={chatTextWrapStyle}
              >
                {isAgui && hasUi ? (
                  <CollapsibleAgentText
                    text={message.text}
                    components={chatMarkdownComponents}
                    minCharsToCollapse={200}
                  />
                ) : (
                  <ReactMarkdown remarkPlugins={chatRemarkPlugins} components={chatMarkdownComponents}>{message.text}</ReactMarkdown>
                )}
              </div>
            </div>
          ) : null}

          {!message.isStreaming ? (
            <VoiceMessagePlayer
              chatId={message.chatId}
              messageId={message.id}
              metadata={message.metadata}
            />
          ) : null}
        </div>

        <p className="text-xs text-gray-500 text-left mt-1 px-1">{formatTimestamp(message.timestamp)}</p>
      </div>
    </div>
  );
};

export default memo(MessageBubble);
